import { getListApiHttpParam, IListState, mapToListModel, objectToFormData } from '@aston/foundation';
import { ISubsidiaryUserStatus } from '@aston/middle-users-admin';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { IFederationForm, IFederationListItem, IFederationUser, IFederationUserApi, IFederationUsersList, IFederationUsersListApi } from '../models';
import { mapToFederationCreateFormApi, mapToFederationUpdateFormApi } from '../functions';
import { AppStoreSelectors } from '../../root-store/app-store';
import { mapToFederationUserItemModel } from '../functions';
import { TenantUrl } from '../../shared-module/functions';
import { FederationUsersListSortProp } from '../enums';

@Injectable({
	providedIn: 'root'
})
export class FederationsService {

	private http = inject(HttpClient)
	private store = inject(Store)

	getFederations(): Observable<IFederationListItem[]> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IFederationListItem[]>(`${config.apiUrl}/api/Federations`)),
		);
	}

	getAllFederations() {
		return this.getFederations();
	}

	getFederation(federationId: number): Observable<IFederationForm> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IFederationForm>(`${config.apiUrl}/api/Federations/${federationId}`)),
		);
	}

	getFederationUsers(federationId: number, params: IListState<FederationUsersListSortProp>): Observable<IFederationUsersList> {
		const httpParams = getListApiHttpParam(params);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IFederationUsersListApi>(`${config.apiUrl}/api/Federations/${federationId}/Users`, {params: httpParams}).pipe(
				map(apiModel => {
					return mapToListModel<IFederationUserApi, IFederationUser>(apiModel, mapToFederationUserItemModel) as IFederationUsersList;
				})
			))
		);
	}

	updateUserStatus(federationId: number, model: ISubsidiaryUserStatus) {
		const params = { status: model.userStatus };
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Federations/${federationId}/Users/${model.userId}/status`, params)),
		);
	}

	postFederationUser(federationId: number, model: IFederationUser) {
		const params = model;
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Federations/${federationId}/users`, params)),
		);
	}

	resendInvitationToUser(federationId: number, userId: number) {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Federations/${federationId}/Users/${userId}/resend-invitation`, null)),
		);
	}

	getTenantUrl(federationId: number): Observable<TenantUrl> {
		const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get(`${config.apiUrl}/api/Federations/${federationId}/users/me/tenant-url`, {
				headers, responseType: 'json'
			})),
			map(url => new TenantUrl(url as string))
		);
	}

	createFederation(model: IFederationForm): Observable<number> {
		const formData = objectToFormData(mapToFederationCreateFormApi(model));
		const headers = new HttpHeaders({ enctype: 'multipart/form-data' });

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<number>(`${config.apiUrl}/api/Federations`, formData, { headers })),
		);
	}

	updateFederation(model: IFederationForm): Observable<void> {
		const formData = objectToFormData(mapToFederationUpdateFormApi(model));
		const headers = new HttpHeaders({ enctype: 'multipart/form-data' });

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Federations/${model.federationId}`, formData, { headers })),
		);
	}

	deleteFederation(federationId: number): Observable<unknown> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post(`${config.apiUrl}/api/Federations/${federationId}/delete`, null)),
		);
	}
}
